import React from 'react';
import PropTypes from 'prop-types';
import { Markup } from 'interweave';
import { store } from '../../store';
import { urlParam } from '../../lib/url';

const tokenReplacement = ({ content, replacementTokens = {} }) => {
  const tokens = {
    retailerName: store.getState()?.retailerSettings?.display_name,
    ...replacementTokens,
  };

  return Object.entries(tokens).reduce(
    (newString, [key, val]) =>
      newString.replaceAll(key.startsWith('$') ? `${key}` : `$${key}`, val),
    content,
  );
};

export const rawString = ({ langKey, content: contentConfig, replacementTokens, fallback }) => {
  const { language } = store.getState();
  let baseText = null;

  if (Array.isArray(langKey)) {
    langKey.some((key) => {
      if (Object.hasOwnProperty.call(language, key)) {
        baseText = language[key];
        return true;
      }
      return false;
    });
  } else {
    baseText = langKey ? language[langKey] : null;
  }

  if (contentConfig) {
    baseText = contentConfig;
  }

  if (!baseText && fallback) {
    baseText = fallback;
  }

  if (!baseText) return '';
  return tokenReplacement({ content: baseText.toString(), replacementTokens });
};

export const Language = ({
  langKey,
  content: contentConfig,
  fallback,
  replacementTokens = {},
  attributes: attr,
  style,
  ...props
}) => {
  if (!langKey && !contentConfig && !fallback) return null;
  try {
    let attributes = attr || {};

    if (style) {
      attributes = {
        ...attributes,
        style,
      };
    }

    // Extract keys that start with any prefix in prefixArray for the attributes obj
    Object.keys(props).forEach((key) => {
      if (['aria-', 'data-'].some((prefix) => key.startsWith(prefix))) {
        attributes[key] = props[key];
        delete props[key];
      }
    });

    // Extract keys that are exact matches for the attributes obj
    Object.keys(props).forEach((key) => {
      if (['role'].some((prefix) => key === prefix)) {
        attributes[key] = props[key];
        delete props[key];
      }
    });

    if (urlParam({ param: 'langKey' })) {
      let output = langKey ? `${langKey}` : 'no associated key';
      if (contentConfig) {
        output = 'Language Key Overridden';
      }
      attributes = {
        ...attributes,
        ['data-lang-key']: output,
        ...{
          ...(Object.keys(replacementTokens).length > 0
            ? { ['data-replacement-tokens']: JSON.stringify(replacementTokens) }
            : {}),
        },
      };
    }
    return (
      <Markup // https://interweave.dev/docs/#props
        {...{
          content: rawString({ langKey, content: contentConfig, replacementTokens, fallback }),
          attributes,
          ...props,
        }}
      />
    );
  } catch (e) {
    console.error(e);
    return null;
  }
};

Language.propTypes = {
  langKey: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  content: PropTypes.string,
  replacementTokens: PropTypes.object,
  attributes: PropTypes.object,
};

Language.defaultProps = {
  langKey: undefined,
  content: undefined,
  replacementTokens: {},
  attributes: {},
};
